import { useState, useEffect } from 'react';
import { Card } from '../../components/common/Card';
import apiClient from '../../services/api';

interface AnalyticsData {
  total_requests: number;
  total_tokens: number;
  total_cost: number;
  active_users: number;
  requests_by_model: Record<string, number>;
  requests_by_provider: Record<string, number>;
}

export function AdminAnalyticsPage() {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/admin/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">数据分析</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">总请求数</h3>
          <p className="mt-2 text-3xl font-bold text-blue-600">
            {analytics?.total_requests?.toLocaleString() || 0}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">总 Token 数</h3>
          <p className="mt-2 text-3xl font-bold text-green-600">
            {analytics?.total_tokens?.toLocaleString() || 0}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">总成本</h3>
          <p className="mt-2 text-3xl font-bold text-purple-600">
            ${analytics?.total_cost?.toFixed(2) || '0.00'}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">活跃用户</h3>
          <p className="mt-2 text-3xl font-bold text-orange-600">
            {analytics?.active_users || 0}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">按模型统计</h3>
          {analytics?.requests_by_model && Object.keys(analytics.requests_by_model).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(analytics.requests_by_model).map(([model, count]) => (
                <div key={model} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{model}</span>
                  <span className="text-sm font-medium">{count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">暂无数据</p>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">按提供商统计</h3>
          {analytics?.requests_by_provider && Object.keys(analytics.requests_by_provider).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(analytics.requests_by_provider).map(([provider, count]) => (
                <div key={provider} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{provider}</span>
                  <span className="text-sm font-medium">{count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">暂无数据</p>
          )}
        </Card>
      </div>
    </div>
  );
}
